import { useMemo } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "@/components/feature/HeroCarousel";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { movies } from "@/mocks/movies";
import { topics } from "@/mocks/topics";
import type { Movie } from "@/types/movie";
import { sortMovies, formatViews } from "@/utils/movieFilters";
import { usePageMeta } from "@/hooks/usePageMeta";

const topicLinks: Record<string, string> = {
  "topic-classic": "/category/movie?genre=%E5%8E%86%E5%8F%B2",
  "topic-hotseries": "/category/tv",
  "topic-highscore": "/category/movie?genre=%E5%89%A7%E6%83%85",
  "topic-scifi": "/category/movie?genre=%E7%A7%91%E5%B9%BB",
  "topic-healing": "/category/tv?genre=%E7%BE%8E%E9%A3%9F",
  "topic-suspense": "/category/movie?genre=%E6%82%AC%E7%96%91",
};

export default function Home() {
  const allMovies = movies as Movie[];

  usePageMeta({
    title: "91在线看 - 高清影视在线播放平台",
    description:
      "91在线看为您提供丰富的电影、电视剧在线播放资源，涵盖最新影视作品和经典大片，满足不同观影需求。畅享轻松观看体验，一站式影视观看选择。",
    keywords:
      "91在线看,在线观看,影视资源,电影在线看,电视剧在线播放,高清影视,免费播放",
  });

  const featured = useMemo(() => allMovies.filter((m) => m.featured).slice(0, 5), [allMovies]);
  const latest = useMemo(() => sortMovies(allMovies, "latest").slice(0, 12), [allMovies]);
  const popular = useMemo(() => sortMovies(allMovies, "views").slice(0, 6), [allMovies]);

  const movieCount = allMovies.filter((m) => m.type === "movie").length;
  const tvCount = allMovies.filter((m) => m.type === "tv").length;
  const totalViews = allMovies.reduce((sum, m) => sum + m.views, 0);

  return (
    <div className="w-full">
      <HeroCarousel movies={featured} />

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12">
        {/* 分类快捷入口 */}
        <section className="pt-12 md:pt-16">
          <SectionHeading
            title="影视分类快捷入口"
            subtitle="按频道快速进入，海量资源一目了然"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Link
              to="/category/movie"
              className="group relative h-56 md:h-64 rounded-lg overflow-hidden cursor-pointer"
            >
              <img
                src="https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20classic%20movie%20theater%20with%20dark%20blue%20seats%20and%20projector%20light%2C%20deep%20blue%20and%20amber%20tones%2C%20moody%20lighting%2C%20no%20text&width=800&height=500&seq=cat-movie&orientation=landscape"
                alt="电影频道"
                title="电影频道 高清在线看"
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7">
                <span className="w-11 h-11 rounded-md bg-primary-500 text-foreground-950 flex items-center justify-center mb-3">
                  <i className="ri-film-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
                  电影频道
                </h3>
                <p className="mt-1 text-sm text-foreground-600">
                  共 {movieCount} 部高清电影 · 科幻 / 动作 / 悬疑 / 剧情
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm text-accent-400 whitespace-nowrap">
                  进入频道
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/category/tv"
              className="group relative h-56 md:h-64 rounded-lg overflow-hidden cursor-pointer"
            >
              <img
                src="https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20modern%20living%20room%20with%20a%20large%20glowing%20television%20screen%20at%20night%2C%20deep%20blue%20and%20warm%20tones%2C%20cozy%20lighting%2C%20no%20text&width=800&height=500&seq=cat-tv&orientation=landscape"
                alt="电视剧频道"
                title="电视剧频道 高清在线看"
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7">
                <span className="w-11 h-11 rounded-md bg-accent-500 text-background-50 flex items-center justify-center mb-3">
                  <i className="ri-tv-2-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
                  电视剧频道
                </h3>
                <p className="mt-1 text-sm text-foreground-600">
                  共 {tvCount} 部热播剧集 · 古装 / 都市 / 刑侦 / 美剧
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm text-accent-400 whitespace-nowrap">
                  进入频道
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </Link>
          </div>
        </section>

        {/* 数据条 */}
        <section className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: "ri-video-line", label: "影视资源总数", value: `${allMovies.length} 部` },
            { icon: "ri-fire-line", label: "累计演示播放量", value: formatViews(totalViews) },
            { icon: "ri-time-line", label: "内容更新频率", value: "每日更新" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-4 rounded-lg bg-background-100 border border-background-200/70 px-5 py-4"
            >
              <span className="w-11 h-11 rounded-md bg-secondary-900/60 text-secondary-200 flex items-center justify-center shrink-0">
                <i className={`${stat.icon} text-2xl`}></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">{stat.label}</p>
                <p className="font-heading text-lg font-bold text-foreground-950">{stat.value}</p>
              </div>
            </div>
          ))}
        </section>

        {/* 最新影视推荐 */}
        <section className="pt-14 md:pt-20">
          <SectionHeading
            title="最新影视推荐"
            subtitle="按上映时间倒序更新，第一时间看新片"
            moreTo="/category/movie"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-7 md:gap-x-5">
            {latest.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        {/* 热门影视精选 */}
        <section className="pt-14 md:pt-20">
          <SectionHeading
            title="热门影视精选"
            subtitle="根据演示播放热度精选，站内最受欢迎的内容"
            moreTo="/category/tv"
            moreLabel="更多热门"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-7 md:gap-x-5">
            {popular.map((movie, i) => (
              <MovieCard key={movie.id} movie={movie} rank={i + 1} />
            ))}
          </div>
        </section>

        {/* 影视专题合集 */}
        <section className="pt-14 md:pt-20">
          <SectionHeading
            title="影视专题合集"
            subtitle="按主题为你整理片单，解决选择困难"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {topics.map((topic) => (
              <Link
                key={topic.id}
                to={topicLinks[topic.id] ?? "/category/movie"}
                className="group relative h-52 rounded-lg overflow-hidden cursor-pointer"
              >
                <img
                  src={topic.image}
                  alt={topic.title}
                  title={`${topic.title} 影视专题`}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/10"></div>
                <div className="absolute inset-0 flex flex-col justify-end p-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {topic.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded text-xs font-label bg-accent-100 text-accent-900"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                    {topic.title}
                  </h3>
                  <p className="mt-1 text-xs md:text-sm text-foreground-600 clamp-2">
                    {topic.description}
                  </p>
                  <p className="mt-2 text-xs text-foreground-500">
                    收录 {topic.count} 部 · 点击查看片单
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}